import React, { FC, useEffect, useState } from 'react';
import Image from 'components/image/Image';
import Button from 'components/Button/Index';
import useLocales from 'hooks/useLocales';
import Navbar from 'components/Navbar';
import ActionButton from 'components/ActionButton/Index';
import ChartComponent from 'components/RadarChart/Index';
import { chartData } from 'core/constants/competencyTabConstants';
import MatchingProfile from 'components/CompanyTabs/MatchingProfile/Index';
import { companyHeader } from 'core/constants/companyHeader';
import { ICompanyHeaderItems } from 'core/interface/headerItems';
import { CompanyTabEnum } from 'core/enum/companyTab';
import PostAJob from 'components/CompanyTabs/PostAJob/Index';
import CultureProfile from 'components/CompanyTabs/CultureProfile/Index';
import CandidateOverview from 'components/CompanyTabs/CandidateOverview/Index';
import Popup from 'components/shared/Popups/Popup/Index';
import AvatarPopup from 'components/shared/Popups/Avatar/Index';
import CareerPage from 'components/CareerPage/Index';
import { IMe } from 'core/interface/me';
import { RootState } from 'redux/store';
import { useSelector } from 'react-redux';

import UpgradePlanPopup from 'components/shared/Popups/UpgradePlan/Index';
import CompanyAvatar from 'components/shared/CompanyAvatar/Index';
import Footer from 'components/Footer/Index';
import { InvitationsService } from 'services/invitations/invitations';

import './Index.scss';
import { isValidEmail } from 'utils/isValidEmail';
import { CompanyService } from 'services/companes/companies';
import CompanyInfoPopup from 'components/shared/Popups/CompanyInfoPopup/Index';
import { Link } from 'react-router-dom';

const Company: FC = (): JSX.Element => {
  const { t } = useLocales();
  const me = useSelector((state: IMe) => state.user).user;
  const { companies } = useSelector((state: RootState) => state.company);
  const [activeTab, setActiveTab] = useState<string>(
    localStorage.getItem('activeTab') ?? CompanyTabEnum.FIND_CANDIDATES
  );
  const [showEditPopup, setShowEditPopup] = useState<boolean>();
  const [showAvatarPopup, setShowAvatarPopup] = useState<boolean>();
  const [showUpgrade, setShowUpgrade] = useState<boolean>(false);
  const [companyEditModal, setCompanyEditModal] = useState<boolean>(false);
  const [inviteEmail, setInviteEmail] = useState<string>('');
  const [inviteEmailError, setInviteEmailError] = useState<string>('');

  const isAdmin = me?.role && me?.role === 'admin';

  useEffect(() => {
    CompanyService.getCompanies(me?.company);
  }, [me?.company]);

  const handleInviteInputChange = (e: string): void => {
    if (e.length > 0) {
      setInviteEmail(e);
      if (!isValidEmail(e)) {
        setInviteEmailError(t('Invalid email'));
      } else {
        setInviteEmailError('');
      }
    } else {
      setInviteEmailError('');
    }
  };

  const handleTabChange = (item: ICompanyHeaderItems) => {
    localStorage.setItem('activeTab', item.value);
    setActiveTab(item.value);
    componentToShow();
  };

  const handleEdit = () => {
    setShowEditPopup(true);
  };

  const handleClosePopup = () => {
    setShowEditPopup(false);
    setShowAvatarPopup(false);
  };

  const componentToShow = (): JSX.Element => {
    switch (activeTab) {
      case CompanyTabEnum.FIND_CANDIDATES:
        return <MatchingProfile />;
      case CompanyTabEnum.POST_A_JOB:
        return <PostAJob />;
      case CompanyTabEnum.CULTURE_PROFILE:
        return <CultureProfile />;
      case CompanyTabEnum.CANDIDATE_OVERVIEW:
        return <CandidateOverview />;
      default:
        return <CareerPage />;
    }
  };

  const onSubmit = (): void => {
    setShowEditPopup(!setShowEditPopup);
  };

  const handleOpenAvatarChange = () => {
    setCompanyEditModal(!companyEditModal);
  };

  const handleSendInvitation = async (): Promise<void> => {
    if (inviteEmail && !inviteEmailError) {
      try {
        await InvitationsService.setInvitations({
          email: inviteEmail,
          role: 'recruiter'
        });
        setInviteEmail(''); // Clear the input after successful invitation
      } catch (error) {
        console.error('Error sending invitation:', error);
      }
    }
  };

  const companyHeaderToShow = isAdmin
    ? companyHeader
    : companyHeader.filter((item) => item.value !== CompanyTabEnum.CAREER_PAGE);

  return (
    <div
      className="companyPage"
      style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}
    >
      <Navbar />
      <div
        className="company"
        style={{ display: 'flex', flexDirection: 'column', flex: 1 }}
      >
        <div className="company__top">
          <div className="company__top__left">
            <div className="company__top__left__image">
              {isAdmin && (
                <ActionButton
                  className="company__top__left__image-edit"
                  iconName="pencil.svg"
                  onClick={handleOpenAvatarChange}
                />
              )}

              <Image
                className="company__top__left-image"
                url={companies?.logo}
                name={'no-images.svg'}
                width={243}
                height={145}
                objectFit="contain"
              />
            </div>
            <div className="company__top__left__info">
              <span className="company__top__left__info-title">
                Welcome {me?.name}
              </span>
              <span className="company__top__left__info-subtitle">
                {companies?.name}
              </span>
              <span className="company__top__left__info-plan">
                {t('You are on the')}{' '}
                {String(companies?.subscription?.plan || 'Free')} plan
              </span>

              <span className="company__top__left__info-credit">
                {t('You can create')}{' '}
                {companies?.subscription?.current_limit?.jobs === 'unlimited'
                  ? `${t('unlimited job posts')} (${t('Fair Use Policy')})`
                  : `${companies?.subscription?.current_limit?.jobs} ${t('job posts')}`
                }{' '}
                {t('and unlock')}{' '}
                {companies?.subscription?.current_limit?.candidates} {t('candidate profiles')}
              </span>
              {isAdmin && (
                <div className="company__top__left__info__buttons">
                  <Button
                    onClick={handleEdit}
                    className="company__top__left__info__buttons-edit button"
                    title={t('Edit Company Info')}
                    t={t}
                  />

                  <Button
                    className="company__top__left__info__buttons-upgrade button"
                    onClick={() => setShowUpgrade(true)}
                    title="Upgrade"
                    t={t}
                  />
                </div>
              )}
            </div>

            {isAdmin && (
              <div className="dashboard__right__top__left">
                <h1
                  className="dashboard__right__top__left-title"
                  onClick={() => {}}
                >
                  {t('invite colleagues')} <Image name="attention.svg" />
                </h1>
                {/* <p className="dashboard__right__top__left-desc">
                 {t('Upgrade or expand your account by inviting at least three of your friends to join you at recrout')}
                </p> */}

                <div className="dashboard__right__top__left__email">
                  <div className="inp">
                    <input
                      onInput={(e) =>
                        handleInviteInputChange(
                          (e.target as HTMLInputElement).value
                        )
                      }
                      className="dashboard__right__top__left__email-email disable-block"
                      type="email"
                      placeholder={t('email address')}
                    />
                    <Button
                      disabled={inviteEmail === '' || inviteEmailError !== ''}
                      t={t}
                      onClick={handleSendInvitation}
                      title="invite"
                      type="submit"
                      className={`dashboard__right__top__left__email-submit disable-block${
                        inviteEmail === '' || inviteEmailError !== ''
                          ? 'deactive'
                          : ''
                      }`}
                    />
                  </div>
                  {inviteEmailError && (
                    <span className="error">{inviteEmailError}</span>
                  )}
                </div>
                <Link
                  className="dashboard__right__top__left__email-invitations"
                  to={'/invitations/'}
                >
                  All invitations
                </Link>
              </div>
            )}
          </div>
        </div>
        <div className="company__header">
          {companyHeaderToShow.map((item, index) => (
            <span
              onClick={() => handleTabChange(item)}
              className={`company__header__item ${
                item.value === activeTab ? 'active' : ''
              }`}
              key={index}
            >
              {item.title}
              <span className="company__header__item-line line"></span>
            </span>
          ))}
        </div>
        <div
          className="main"
          style={{ flex: 1, display: 'flex', flexDirection: 'column' }}
        >
          {componentToShow()}
        </div>
      </div>
      {showEditPopup && (
        <CompanyInfoPopup onSubmit={onSubmit} onClose={handleClosePopup} />
      )}
      {showAvatarPopup && (
        <Popup onClose={handleClosePopup} title={t('Edit Your Profile Image')}>
          <AvatarPopup id={me.id} onPopupClose={handleClosePopup} t={t} />
        </Popup>
      )}
      {companyEditModal && (
        <Popup
          onClose={handleOpenAvatarChange}
          title={t('Edit Your Profile Image')}
        >
          <CompanyAvatar
            id={me.id}
            onPopupClose={handleOpenAvatarChange}
            t={t}
            selectedCompanyEdit={companies}
          />
        </Popup>
      )}
      {showUpgrade && (
        <Popup
          className="upgrade-plan-nav upgrade-popup-nav"
          title={t('Upgrade your plan')}
          description={t('Your subscription plan determines how many profiles you can unlock and how many new jobs you can post each month. Once you use up these credits, they won’t be replenished - even if you delete a job post. Any unused credits at the end of your billing cycle will roll over to the next month, so you don’t lose them.')}
          onClose={() => setShowUpgrade(false)}
        >
          <UpgradePlanPopup t={t} />
        </Popup>
      )}
      <Footer />
    </div>
  );
};

export default Company;
